<script lang="ts" setup>
// @ts-expect-error JS library
import { useMq } from 'vue3-mq'
import { storeToRefs } from 'pinia'
import type { Skill, Topic } from '@prisma/client'
import { useRoot } from '~/store'
import { useRadar } from '~/store/radar'
import { useLocaleThemePath } from '~/composables/locale'
import { capitalize } from '~/utils'
import { articles } from '~/config/articles'

const page = '_article'
const $radar = useRadar()
const { routeRaw } = storeToRefs($radar)
const { setWheelsCount } = useRoot()
const { xs, sm } = useMq()
const { t } = useI18n()
const i18nHead = useLocaleHead()

const slug = routeRaw.value?.params?.article as string
const article = computed(() => articles.find((a) => a.slug === slug))


if (!article.value) {
  const event = useRequestEvent()
  if (event) setResponseStatus(event, 404, 'Article not found.')
}

const { data: markdown, refresh } = await useAsyncData<string | null>('markdown', async () => {
  const blob = article.value?.location ? await $fetch<Blob>(article.value.location) : null
  return blob ? await blob?.text() : null
})

$radar.setPreviousRoutePath('/assets')

useSeoMeta({
  title: () => t(`routes.${page}.title`, { name: capitalize(article.value?.name) }),
  ogTitle: () => t(`routes.${page}.title`, { name: capitalize(article.value?.name) }),
  description: () => t(`routes.${page}.description`, { name: capitalize(article.value?.name) }),
  ogDescription: () => t(`routes.${page}.description`, { name: capitalize(article.value?.name) })
})

useHead(i18nHead.value)

useStructuredData(page)
setWheelsCount(2)
</script>

<template lang="pug">
section.article__details.container-fluid
  .row(v-if="article")
    .col-12
      .article__details__hero
        h1 {{ article.name }}
        NuxtImg(
          v-if="article.preview"
          :src="article.preview"
          provider="s3"
          :alt="$t('components.article-card.image.alt', { video: article.name} )"
          :title="$t('components.article-card.image.title', { video: article.name} )"
          :aria-label="$t('components.article-card.image.aria-label', { video: article.name} )"
          width="350"
          height="200"
        )
  .row(v-if="article")
    .col-12
      .article__details__content
        UIMarkdownReader.w-full(
          :markdown="markdown"
        )
  .row(v-else)
    .col-12
      .article__404
        h1 Error 404 - Article Not Found.
        p The server failed to retrieve the documentation:
          span {{ location }}.
        UIGridImage(
          src="https://s3.rottigni.tech/portfolio.rottigni.tech/static_content/404/hero-404-2.jpg"
          :height="4"
        )
        NuxtLink(:to="useLocaleThemePath(`/`)") Home
</template>

<style lang="sass" scoped>
.row
  @apply w-full h-[100dvh] overflow-hidden relative
.article__details
    @apply w-full h-full flex-center flex-col
    h1
      @apply text-secondary text-center text-2xl lg:text-4xl
    .article__details__hero,
    .article__details__content
      @apply w-full h-full flex-center flex-col gap-8
      img
        @apply w-full h-auto max-h-[60vh] object-cover object-center
.article__404
  @apply flex h-full flex-col justify-center relative
  h1
    @apply text-primary text-center text-2xl lg:text-4xl pb-6
  p
    @apply w-full text-lg lg:text-2xl text-white text-center max-w-[600px] mx-auto
    span
      @apply text-secondary mx-1
  a
    @apply block w-full text-center text-primary underline text-2xl
</style>
